import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageProps, Organization } from "../types/auth";

import styles from './styles/SettingsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const SettingsPage: React.FC<PageProps> = ({ user, token, selectedOrgID }) => {
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<Partial<Organization>>({
    org_id: selectedOrgID || 0,
    name: "",
    email: "",
    role: ""
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is an owner
    const selectedOrgDetails = user?.organizations?.find(org => org.org_id === selectedOrgID);
    if (!selectedOrgDetails || selectedOrgDetails.role !== "owner") {
      navigate("/home");
      return;
    }

    // Fetch organization details from API
    const fetchOrgDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/organizations/${selectedOrgID}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch organization details");
        }

        const orgData = await response.json();
        setOrganization({
          ...orgData,
          role: selectedOrgDetails.role // Keep the role from the user's data
        });
        setLoading(false);
      } catch (err) {
        setError("Failed to load organization details. Please try again later.");
        setLoading(false);
      }
    };

    fetchOrgDetails();
  }, [user, selectedOrgID, navigate, token]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setOrganization(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!organization.name?.trim()) {
      setError("Organization name cannot be empty.");
      return;
    }

    // Basic email validation
    if (organization.email && !validateEmail(organization.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/organizations/${selectedOrgID}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
        body: JSON.stringify({ 
          name: organization.name,
          email: organization.email 
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to update organization settings.");
        return;
      }

      setSuccess("Organization settings updated successfully!");
      setError("");

      // Refresh the page after a short delay to show updated org name in the header
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  // Simple email validation function
  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  if (loading) {
    return <div className={styles.loadingContainer}>
      <p>Loading organization details...</p>
    </div>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Organization Settings</h1>
      {error && <p className={styles.errorMessage}>{error}</p>}
      {success && <p className={styles.successMessage}>{success}</p>}
      <form onSubmit={handleSave} className={styles.form}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Organization Name:</label>
          <input
            type="text"
            name="name"
            value={organization.name || ""}
            onChange={handleInputChange}
            required
            className={styles.input}
            placeholder="Enter organization name"
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>Organization Email:</label>
          <input
            type="email"
            name="email"
            value={organization.email || ""}
            onChange={handleInputChange}
            className={styles.input}
            placeholder="Enter organization email (optional)"
          />
        </div>
        <button type="submit" className={styles.submitButton}>Save Changes</button>
      </form>
    </div>
  );
};

export default SettingsPage; 